import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ServerPagination from '../components/ServerPagination';
import Filters from '../components/Filters';
import AccordionList from '../components/AccordionList';
import ExportButtons from '../components/ExportButtons';
import PageSizeSelector from '../components/PageSizeSelector';
import { getUsersWithFilters, setFilters, resetFilters } from '../redux/userSlice';
import { fetchUsersWithFilters } from '../services/api';
import { handleExportResponse } from '../utils/exportUtils';
import OrganizationDetailModal from '../components/OrganizationDetailModal';
import { EyeIcon } from '@heroicons/react/16/solid';
import { toggleVerifiedStatus, toggleDeletedStatus } from '../services/api';
import RecordsLoader from '../components/RecordsLoader';
import ConfirmationDialog from '../components/ConfirmationDialog';

const OrganizationList = () => {
    const dispatch = useDispatch();
    const { users: organizations, status, pagination, error } = useSelector((state) => state.users);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [showFilters, setShowFilters] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState({ verified: {}, deleted: {} });

    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(() => () => {});

    const [localFilters, setLocalFilters] = useState({
        email: '',
        first_name: '',
        last_name: '',
        organization_title: '',
        organization_type: '',
        country: '',
        city: '',
        verified_organization: '',
        deleted: '',
        subscribe_status: '',
    });

    // Filter configuration
    const filterConfig = [
        {
            key: 'email',
            label: 'Email',
            type: 'text',
            placeholder: 'Enter email',
        },
        {
            key: 'first_name',
            label: 'First Name',
            type: 'text',
            placeholder: 'Enter first name',
        },
        {
            key: 'last_name',
            label: 'Last Name',
            type: 'text',
            placeholder: 'Enter last name',
        },
        {
            key: 'organization_title',
            label: 'Organization Title',
            type: 'text',
            placeholder: 'Enter organization title',
        },
        {
            key: 'organization_type',
            label: 'Organization Type',
            type: 'text',
            placeholder: 'Enter organization type',
        },
        {
            key: 'country',
            label: 'Country',
            type: 'text',
            placeholder: 'Enter country',
        },
        {
            key: 'city',
            label: 'City',
            type: 'text',
            placeholder: 'Enter city',
        },
        {
            key: 'verified_organization',
            label: 'Verified',
            type: 'select',
            options: [
                { value: '1', label: 'Verified' },
                { value: '0', label: 'Not Verified' },
            ],
        },
        {
            key: 'deleted',
            label: 'Status',
            type: 'select',
            options: [
                { value: '0', label: 'Active' },
                { value: '1', label: 'Deleted' },
            ],
        },
        {
            key: 'subscribe_status',
            label: 'Subscribe Status',
            type: 'select',
            options: [
                { value: 'subscribed', label: 'Subscribed' },
                { value: 'unsubscribed', label: 'Unsubscribed' },
            ],
        },
    ];

    // Fetch organizations with current filters and pagination
    const fetchOrganizations = useCallback(() => {
        const params = {
            user_type: 'organization',
            page: currentPage,
            page_size: pageSize,
            ...localFilters,
        };
        
        // Remove empty filter values
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });

        dispatch(getUsersWithFilters(params));
    }, [dispatch, currentPage, pageSize, localFilters]);

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]);

    const handleFilterChange = (filters) => {
        setLocalFilters(filters);
        dispatch(setFilters(filters));
        setCurrentPage(1);
    };

    const handleFilterReset = (filters) => {
        setLocalFilters(filters);
        dispatch(resetFilters());
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleExport = async (format) => {
        try {
            const exportParams = {
                user_type: 'organization',
                ...localFilters,
                export: format,
            };
            
            Object.keys(exportParams).forEach(key => {
                if (exportParams[key] === '' || exportParams[key] === null || exportParams[key] === undefined) {
                    delete exportParams[key];
                }
            });

            const response = await fetchUsersWithFilters(exportParams);
            handleExportResponse(response, format, `organizations.${format}`);
        } catch (error) {
            console.error('Export error:', error);
            alert(error.message || 'Failed to export organizations. Please try again.');
        }
    };

    const handleRowClick = (org) => {
        setSelectedUser(org);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    const openConfirmationDialog = (message, action) => {
        setConfirmationMessage(message);
        setConfirmAction(() => action);
        setIsConfirmationOpen(true);
    };

    const closeConfirmationDialog = () => {
        setIsConfirmationOpen(false);
    };

    const handleToggleVerified = (orgId, currentStatus) => {
        openConfirmationDialog(
            `Are you sure you want to ${currentStatus ? 'disapprove' : 'approve'} this organization?`,
            async () => {
                setLoading(prev => ({
                    ...prev,
                    verified: { ...prev.verified, [orgId]: true }
                }));
                try {
                    await toggleVerifiedStatus(orgId, 'organization');
                    fetchOrganizations(); // Refresh the list
                } catch (error) {
                    console.error('Error toggling verified status:', error);
                } finally {
                    setLoading(prev => ({
                        ...prev,
                        verified: { ...prev.verified, [orgId]: false }
                    }));
                    closeConfirmationDialog();
                }
            }
        );
    };

    const handleToggleDeleted = (orgId, currentStatus) => {
        openConfirmationDialog(
            `Are you sure you want to ${currentStatus ? 'activate' : 'deactivate'} this organization?`,
            async () => {
                setLoading(prev => ({
                    ...prev,
                    deleted: { ...prev.deleted, [orgId]: true }
                }));
                try {
                    await toggleDeletedStatus(orgId, 'organization');
                    fetchOrganizations(); // Refresh the list
                } catch (error) {
                    console.error('Error toggling deleted status:', error);
                } finally {
                    setLoading(prev => ({
                        ...prev,
                        deleted: { ...prev.deleted, [orgId]: false }
                    }));
                    closeConfirmationDialog();
                }
            }
        );
    };

    return (
        <div className="container mx-auto p-6">
            {status === 'loading' && <RecordsLoader text="Loading Organizations, please hold on..." />}

            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl text-black font-bold">Organizations List</h1>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium flex items-center gap-2"
                >
                    <svg
                        className={`w-5 h-5 transition-transform duration-200 ${showFilters ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="mb-4">
                    <Filters
                        filters={filterConfig}
                        onFilterChange={handleFilterChange}
                        onReset={handleFilterReset}
                    />
                </div>
            )}

            {/* Export Buttons */}
            <div className="mb-4">
                <ExportButtons
                    onExport={handleExport}
                    isLoading={status === 'loading'}
                />
            </div>

            {/* Page Size Selector */}
            <div className="mb-4">
                <PageSizeSelector
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            {status === 'failed' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    Error loading Organizations: {error || 'Unknown error'}
                </div>
            )}

            {/* Organizations Accordion List */}
            {status === 'succeeded' && (
                <>
                    <AccordionList
                        data={organizations || []}
                        getRowKey={(org) => org.id}
                        primaryFields={[
                            {
                                key: 'index',
                                label: '#',
                                render: (value, item, index) => {
                                    // Calculate descending index based on current page and page size
                                    const page = pagination?.page || currentPage;
                                    const pageSizeValue = pagination?.page_size || pageSize;
                                    const totalCount = pagination?.total_count || 0;
                                    const startIndex = (page - 1) * pageSizeValue;
                                    return totalCount - (startIndex + index);
                                },
                            },
                            {
                                key: 'name',
                                label: 'Name',
                                render: (value, item) => `${item.first_name || ''} ${item.last_name || ''}`.trim() || '—',
                                flex: true,
                            },
                            {
                                key: 'email',
                                label: 'Email',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'organization_title',
                                label: 'Title',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'organization_type',
                                label: 'Type',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'country',
                                label: 'Country',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'paid_status',
                                label: 'Status',
                                render: (value) => value === 2 ? 'Paid' : 'Free',
                            },
                        ]}
                        expandedFields={[
                            {
                                key: 'city',
                                label: 'City',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'verified_organization',
                                label: 'Verified',
                                render: (value) => value ? 'Yes' : 'No',
                            },
                            {
                                key: 'deleted',
                                label: 'Deleted',
                                render: (value) => value ? 'Yes' : 'No',
                            },
                            {
                                key: 'subscribe_status',
                                label: 'Subscribe Status',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'profile_picture',
                                label: 'Profile Picture',
                                render: (value) => value ? (
                                    <img src={value} alt="Profile" className="w-16 h-16 rounded-full object-cover" />
                                ) : '—',
                            },
                            {
                                key: 'featured_image',
                                label: 'Banner Image',
                                render: (value) => value ? (
                                    <img src={value} alt="Banner" className="w-32 h-16 rounded object-cover" />
                                ) : '—',
                            },
                        ]}
                        renderActions={(org, isExpanded) => (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRowClick(org);
                                    }}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 transition-all duration-200 hover:scale-110 border border-blue-200"
                                    title="View Details"
                                >
                                    <EyeIcon className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleVerified(org.id, org.verified_organization);
                                    }}
                                    className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 border ${
                                        org.verified_organization
                                            ? 'bg-green-50 hover:bg-green-100 text-green-700 border-green-200'
                                            : 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
                                    }`}
                                    title={org.verified_organization ? 'Disapprove' : 'Approve'}
                                    disabled={loading.verified[org.id]}
                                >
                                    {loading.verified[org.id] ? (
                                        <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-current border-t-transparent"></div>
                                    ) : (
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            {org.verified_organization ? (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            ) : (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            )}
                                        </svg>
                                    )}
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleDeleted(org.id, org.deleted);
                                    }}
                                    className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 border ${
                                        org.deleted
                                            ? 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
                                            : 'bg-green-50 hover:bg-green-100 text-green-700 border-green-200'
                                    }`}
                                    title={org.deleted ? 'Activate' : 'Deactivate'}
                                    disabled={loading.deleted[org.id]}
                                >
                                    {loading.deleted[org.id] ? (
                                        <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-current border-t-transparent"></div>
                                    ) : (
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            {org.deleted ? (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                                                />
                                            ) : (
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            )}
                                        </svg>
                                    )}
                                </button>
                            </>
                        )}
                        emptyMessage="No organizations found!"
                        allowMultipleExpand={true}
                    />

                    {/* Server-side Pagination */}
                    {pagination && pagination.total_pages > 0 && (
                        <ServerPagination
                            currentPage={pagination.page || currentPage}
                            totalPages={pagination.total_pages || 1}
                            onPageChange={handlePageChange}
                            totalCount={pagination.total_count || 0}
                            pageSize={pagination.page_size || pageSize}
                            showPageInfo={true}
                        />
                    )}
                </>
            )}

            {/* Modal for Organization Details */}
            <OrganizationDetailModal isOpen={isModalOpen} onClose={closeModal} user={selectedUser} />

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                isOpen={isConfirmationOpen}
                message={confirmationMessage}
                onConfirm={confirmAction}
                onCancel={closeConfirmationDialog}
            />
        </div>
    );
};

export default OrganizationList;
